import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

/**
 * The motion this dashboard is allowed to make.
 *
 * Three rules, and every helper here follows them:
 *
 *   The number on screen is the number from the API. A count-up tweens towards
 *   the real value and lands exactly on it; it never rounds its way to
 *   something else, and the accessible name carries the final value from the
 *   first frame so a screen reader is never read a running total.
 *
 *   Nothing is hidden by CSS. Entrances are set up in JS, so if the animation
 *   never runs — reduced motion, a GSAP failure, an old browser — the panel is
 *   simply there rather than invisible.
 *
 *   Reduced motion means arrival, not absence: the same final state, reached
 *   without travel.
 */

export const EASE = "power3.out";

export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * A number that counts up to `value` and, when the value changes, carries on
 * from where it is rather than starting again from zero.
 */
export function useCountUp(value, { format = (n) => Math.round(n).toLocaleString(), duration = 1.1 } = {}) {
  const [text, setText] = useState(() => format(value ?? 0));
  const state = useRef({ current: 0 });

  useEffect(() => {
    const target = Number(value) || 0;
    if (prefersReducedMotion()) {
      state.current.current = target;
      setText(format(target));
      return undefined;
    }
    const tween = gsap.to(state.current, {
      current: target,
      duration,
      ease: EASE,
      onUpdate: () => setText(format(state.current.current)),
      // Land on the real value, never on the last animation frame's rounding.
      onComplete: () => setText(format(target)),
    });
    return () => tween.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return text;
}

/**
 * Bars rise from their baseline.
 *
 * Animates transform only, never a geometry attribute. React owns height and
 * width: tweening them meant a re-render with new data was reverted to the
 * previous render's value by the animation's own cleanup, which is how a
 * chart ended up drawn with every bar at zero. Transform is never written by
 * React, so the animation can come and go without ever touching the data.
 */
export function useBarsRise(scope, deps = [], { selector = "[data-bar]", axis = "y", stagger = 0.018 } = {}) {
  useGSAP(
    () => {
      if (prefersReducedMotion()) return;
      const bars = gsap.utils.toArray(selector);
      if (!bars.length) return;
      gsap.from(bars, {
        [axis === "y" ? "scaleY" : "scaleX"]: 0,
        transformOrigin: axis === "y" ? "50% 100%" : "0% 50%",
        duration: 0.7,
        ease: EASE,
        stagger,
        clearProps: "transform",
      });
    },
    { scope, dependencies: deps, revertOnUpdate: true },
  );
}

/**
 * Ring segments sweep round to their length. The CSS property, not the SVG
 * attribute, for the same reason as above: clearing the inline style hands the
 * segment straight back to whatever React last rendered.
 */
export function useRingSweep(scope, deps = [], selector = "[data-ring]") {
  useGSAP(
    () => {
      if (prefersReducedMotion()) return;
      gsap.utils.toArray(selector).forEach((el, i) => {
        const length = Number((el.getAttribute("stroke-dasharray") || "0").split(" ")[0]) || 0;
        if (!length) return;
        gsap.fromTo(
          el,
          { strokeDashoffset: length },
          { strokeDashoffset: 0, duration: 0.9, ease: EASE, delay: i * 0.08, clearProps: "strokeDashoffset" },
        );
      });
    },
    { scope, dependencies: deps, revertOnUpdate: true },
  );
}

/** Strokes that draw themselves, without a plugin. */
export function useDrawLines(scope, selector, deps = []) {
  useGSAP(
    () => {
      if (prefersReducedMotion()) return;
      const paths = gsap.utils.toArray(selector);
      paths.forEach((path, i) => {
        const length = typeof path.getTotalLength === "function" ? path.getTotalLength() : 0;
        if (!length) return;
        gsap.fromTo(
          path,
          { strokeDasharray: length, strokeDashoffset: length },
          {
            strokeDashoffset: 0,
            duration: 1.1,
            ease: "power2.inOut",
            delay: 0.15 + i * 0.04,
            clearProps: "strokeDasharray,strokeDashoffset",
          },
        );
      });
    },
    { scope, dependencies: deps, revertOnUpdate: true },
  );
}
